<template>
  <component
    :is="type"
    :class="['top-bar', { 'top-bar--sticky': sticky, 'top-bar__platinum': isPlatinum }]"
    v-chakra="{
      '@media print': {
        display: 'none !important',
      },
    }"
    :p="{ base: '0', sm: '34px 16px' }"
  >
    <c-flex alignItems="center">
      <button
        d="flex"
        alignItems="center"
        :class="['nav__opener', { 'nav__opener--active': isOpen }]"
        @click="toggleSidebar()"
      >
        <c-icon
          name="burger_menu"
          :aria-label="`${!isOpen ? 'Open' : 'Close'} navigation`"
          :color="textColor"
          size="1.75rem"
          ml="xs"
          :mr="['xs', 'xs', 0]"
        />
      </button>

      <c-text as="span" :class="['top-bar__title', { 'top-bar__title__platinum': isPlatinum }]">
        <a
          v-if="titleType === 'routerLink'"
          :class="['top-bar__title__link', { 'top-bar__title__link__platinum': isPlatinum }]"
          :href="titleHref"
          @click.prevent.stop="onNavigate(titleHref)"
          v-html="title"
        ></a>
        <a v-else class="top-bar__title__link" :href="titleHref" v-html="title"></a>
      </c-text>

      <c-box
        v-if="showPartnerOverlay"
        :d="{ base: 'none', lg: 'flex' }"
        ml="clamp(var(--chakra-space-lg), 4vw, var(--chakra-space-4xl))"
      >
        <TopNavPartnerOverlayConnector />
      </c-box>

      <GlobalSearchField
        v-chakra
        :d="{ base: 'none', md: 'flex' }"
        :tier="tier"
        ml="clamp(var(--chakra-space-lg), 4vw, var(--chakra-space-4xl))"
      ></GlobalSearchField>
      <GlobalSearchOverlay></GlobalSearchOverlay>
    </c-flex>

    <c-box class="d-flex justify-content-end align-items-center">
      <c-box
        :d="{ base: 'inline-block', md: 'none' }"
        cursor="pointer"
        @click="toggleGlobalSearchOverlay"
        @keydown.enter="toggleGlobalSearchOverlay"
        tabindex="0"
      >
        <c-icon name="search" size="2rem" :color="textColor" />
      </c-box>

      <ul class="nav top-bar__nav">
        <li
          v-for="(item, index) in navItems"
          :key="index"
          :class="['top-bar__nav__item', { 'top-bar__nav__item--active': item.active }]"
          v-chakra
          m="0"
          p="0"
          d="inline-block"
        >
          <a
            v-if="item.type === 'routerLink'"
            class="top-bar__nav__link"
            :href="item.href"
            :data-cy="`navlink-${item.id || index}`"
            @click.prevent.stop="onNavigate(item.href)"
          >
            <c-icon
              :name="item.icon"
              :color="item.active ? activeLinkColor : textColor"
              size="2.25rem"
            />
            <c-text
              as="span"
              v-if="item.count"
              backgroundColor="brand.yellow"
              color="black"
              position="absolute"
              top="2px"
              right="5px"
              borderRadius="full"
              p="3px 0 0 0"
              w="16px"
              h="16px"
              lineHeight="none"
              textAlign="center"
              fontSize="xs"
            >
              {{ item.count }}
            </c-text>
          </a>
          <a v-else class="top-bar__nav__link" :href="item.href">
            <Icon
              :name="item.icon"
              :aria-label="item.name"
              fill="#fff"
              size="medium"
              :count="item.count"
              :label="item.label"
            />
          </a>
        </li>
      </ul>

      <ProfileMenuReactConnector
        v-if="partnerInfo && partnerInfo.partnerInfo && partnerInfo.partnerInfo.partnerId"
      />
    </c-box>
  </component>
</template>

<script>
import Icon from "../elements/Icon.vue";
import { mapState } from "vuex";
import { CIcon, CBox, CFlex, CInput, CText } from "@chakra-ui/vue";
import ProfileMenuReactConnector from "../../views/ProfileMenuReactConnector.vue";
import GlobalSearchField from "../../components/globalSearch/GlobalSearchField.vue";
import GlobalSearchOverlay from "@/components/globalSearch/GlobalSearchOverlay";
import { PartnerTier } from "@iloq/shared/IPartnerInfo";
import TopNavPartnerOverlayConnector from "../../views/TopNavPartnerOverlayConnector.vue";
import { CorePermission } from "@iloq/shared/CorePermission";

/**
 * Shortcut navigation
 */
export default {
  name: "TopNav",
  components: {
    Icon,
    CIcon,
    CBox,
    CFlex,
    CInput,
    CText,
    ProfileMenuReactConnector,
    GlobalSearchField,
    GlobalSearchOverlay,
    TopNavPartnerOverlayConnector,
  },
  status: "prototype",
  release: "0.0.1",
  model: {
    prop: "active",
  },
  props: {
    /**
     * The html element name used for the nav bar.
     */
    type: {
      type: String,
      default: "header",
    },
    /**
     * Title text
     */
    title: {
      type: String,
      default: "Title",
    },
    /**
     * Title href
     */
    titleHref: {
      type: String,
      default: "#",
    },
    titleType: {
      type: String,
      default: undefined,
    },
    /**
     * Top nav items
     */
    navItems: {
      required: true,
      type: Array,
    },

    /**
     * Sticky
     **/
    sticky: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      scrollPosition: 0,
      isOpen: true,
    };
  },
  computed: {
    ...mapState(["user"]),
    ...mapState(["partnerInfo"]),
    textColor() {
      return this.isPlatinum ? "brand.purple" : "white";
    },
    activeLinkColor() {
      return this.isPlatinum ? "brand.purple" : "brand.beige";
    },
    tier() {
      return this.partnerInfo.partnerInfo.partnerTier;
    },
    isPlatinum() {
      return this.tier === PartnerTier.PLATINUM;
    },
    showPartnerOverlay() {
      return this.tier !== PartnerTier.NONE;
    },
  },
  mounted() {
    if (this.$bus) {
      this.$bus.$on("sidebarOpen", (isOpen) => (this.isOpen = isOpen));
    }
    window.addEventListener("scroll", this.updateScroll);
  },

  methods: {
    updateScroll() {
      this.scrollPosition = window.scrollY;
    },

    openSidebar() {
      this.$bus.$emit("sidebarOpen", true);
      this.isOpen = true;
    },
    closeSidebar() {
      this.$bus.$emit("sidebarOpen", false);
      this.isOpen = false;
    },
    toggleSidebar() {
      if (this.isOpen) {
        this.closeSidebar();
      } else {
        this.openSidebar();
      }
    },
    onNavigate(path) {
      // Re-emit
      this.$emit("navigate", path);
    },
    toggleGlobalSearchOverlay() {
      this.$store.commit("globalSearch/toggleOverlayVisibilityOnMobile");
    },
  },
};
</script>

<style lang="scss">
@import "../../design-system/assets/tokens/tokens";
// Design Tokens with local scope
$brand-purple: #0d0f22;
$color-nav-link: $color-white;
$color-nav-link-platinum: $brand-purple;

.top-bar {
  height: 52px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1920px;
  box-sizing: border-box;
  font-family: $font-text;
  font-size: $size-s;
  line-height: $line-height-m;
  color: $color-white;
  vertical-align: middle;
  font-weight: 500;
  text-align: left;
  overflow: hidden;
  background-color: $brand-purple;

  &__platinum {
    background:
      radial-gradient(60% 120% at 25% 50%, #f0efef 0%, rgba(133, 133, 133, 0.3) 100%), #ececec;
    color: $color-nav-link-platinum;
  }

  .nav__opener {
    cursor: pointer;
    border: 0;
    padding: 4px;
    margin: 0;
    background: none;
    outline: 0;
    z-index: $z-index-modal;

    &:hover {
      opacity: 0.8;
    }
  }

  .icon {
    margin: 0;
  }

  &__title {
    display: none;
    margin-top: 0;

    &__link {
      text-decoration: none;
      font-size: 22px;
      color: $color-nav-link;
      transition: all $duration-default ease-in-out;
      max-width: 218px;

      svg {
        width: 100%;
        height: auto;
      }

      &:hover {
        color: $color-nav-link;
        opacity: 0.8;
      }
      &__platinum {
        color: $color-nav-link-platinum;
        &:hover {
          color: $color-nav-link-platinum;
          opacity: 0.8;
        }
      }
      img {
        height: 24px;

        @include media-min-width("md") {
          height: 32px;
        }
      }
    }
  }
  .top-bar__nav {
    display: flex;
    align-items: center;
    list-style-type: none;
    margin: 0;
    top: 0;
    position: relative;
    float: right;
    padding: 0;

    &__link {
      transform: scale(0.9);
      color: $color-nav-link;
      padding-top: 3px;
      padding-bottom: 3px;
      padding-right: 10px;
      padding-left: 10px;
      margin-top: 0;
      margin-right: 0;
      margin-left: 0;
      text-decoration: none;
      display: inline-block;
      transition: opacity 0.2s ease-in-out;
      &:hover {
        opacity: 0.8;
      }
      &__platinum {
        color: $color-nav-link-platinum;
      }
    }
  }
  &--sticky {
    top: 0;
    position: fixed;
    z-index: $z-index-modal;
  }
}

.top-bar {
  &__title {
    display: flex;
    align-items: center;
    margin-top: 0;
    &__link {
      display: flex;
      align-items: center;
    }
  }

  .nav__opener {
    .icon--menu {
      padding: 5px 5px 5px 5px;
      width: 40px;
      height: 40px;
    }
  }
}

@media (min-width-for("md")) {
  .top-bar {
    &__title {
      margin-left: 15px;
    }
  }
}

@media (min-width-for("lg")) {
  .top-bar {
    padding-right: 20px;
    .top-bar__nav {
      text-align: right;
    }
    &__title {
      display: flex;
      align-items: center;
      font-size: 20px;
      text-decoration: none;
      color: #fff;
      font-weight: $weight-normal;
      line-height: 30px;
      margin-left: 0;
      margin-top: 0;
      padding: 0 0 0 $space-s;
      vertical-align: middle;
    }
  }
}
</style>
